import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/shared/hooks";
import { selectActiveProjectId } from "@/features/settings";
import { useReportsPage } from "../../hooks";
import {
  ReportsHeader,
  TrialBalanceSection,
  ReportsPageSkeleton,
  JournalEntryDetailModal,
} from "./components";

const JournalEntriesTable = lazy(() =>
  import("./components/JournalEntriesTable").then((m) => ({ default: m.default })),
);

export default function ReportsPage() {
  const { t } = useTranslation("ReportsPage");
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const activeProjectId = useAppSelector(selectActiveProjectId);

  const {
    dateRange,
    setDateRange,
    trialBalance,
    journalEntries,
    isLoading,
    isFetchingAny,
    trialBalanceError,
    journalEntriesError,
    isLoadingJournalEntries,
  } = useReportsPage({ projectId: activeProjectId });

  if (isLoading) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <ReportsHeader dateRange={dateRange} onDateRangeChange={setDateRange} />
          <ReportsPageSkeleton />
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <ReportsHeader dateRange={dateRange} onDateRangeChange={setDateRange} />

        <TrialBalanceSection
          data={trialBalance}
          isLoading={isFetchingAny}
          error={trialBalanceError}
        />

        <Suspense fallback={<ReportsPageSkeleton />}>
          <JournalEntriesTable
            data={journalEntries}
            isLoading={isLoadingJournalEntries || isFetchingAny}
            error={journalEntriesError}
            onEntryClick={setSelectedEntryId}
          />
        </Suspense>
      </section>

      {selectedEntryId !== null && (
        <JournalEntryDetailModal entryId={selectedEntryId} onClose={() => setSelectedEntryId(null)} />
      )}
    </>
  );
}
